import React from "react";
// Step-6 visual language (purple-outline bank card) + the finish-stripe page
// shell. Both stylesheets are safe to double-import — every consumer of this
// component already pulls them in via its own page styles.
import "../pages/onboarding/OnboardingPage.css";
import "../pages/FinishStripePage.css";
import { t } from "../i18n";
import { T } from "../i18n/T";

interface Props {
  /** Big line inside the purple card, e.g. "Add bank details". */
  ctaTitle: string;
  /** One short line under the CTA title. */
  ctaSub: React.ReactNode;
  onCta: () => void;
  /** Swaps the CTA title while an action is in flight. */
  pending?: boolean;
  pendingTitle?: string;
  error?: string;
  onBack: () => void;
}

/**
 * The ONE "finish setting up your account" screen (punch list,
 * founder-approved). Every door that used to land on a bespoke
 * Stripe screen renders this exact layout:
 *   1. StripeCallbackPage "incomplete"  — Stripe sent the talent back mid-setup
 *   2. StripeCallbackPage "needs_login" — callback couldn't identify them
 *   3. FinishStripePage — login-resume / welcome-email resume (needsStripeFinish)
 * No greeting, no name — the copy works whether or not we know who they are.
 * Back is supplied by the host page (homepage; authenticated hosts sign out
 * first so the homepage isn't half-logged-in).
 */
const FinishSetupScreen: React.FC<Props> = ({
  ctaTitle,
  ctaSub,
  onCta,
  pending = false,
  pendingTitle,
  error,
  onBack,
}) => (
  <div className="finish-stripe-container">
    <div className="finish-stripe-inner">
      <div className="finish-stripe-back-row">
        <button
          type="button"
          className="step5-back"
          onClick={onBack}
          aria-label={t("finishSetupScreen.backToHomepage")}
        >
          <T k="finishSetupScreen.back" tags={{ 0: (c) => <span aria-hidden="true">{c}</span> }} />
        </button>
      </div>

      <h1 className="finish-stripe-title">
        <T k="finishSetupScreen.letSFinishSetting" tags={{ 0: <br />, 1: (c) => <span className="finish-stripe-title-accent">{c}</span> }} />
      </h1>

      <p className="finish-stripe-body">
        {t("finishSetupScreen.youReAlmostThere")}
      </p>

      {error && (
        <p className="finish-stripe-error" role="alert">
          {error}
        </p>
      )}

      <div className="step5-choice-grid finish-stripe-cta-grid">
        <button
          type="button"
          className="step5-choice step5-choice--stripe"
          onClick={onCta}
          disabled={pending}
          aria-busy={pending}
        >
          <div className="step5-choice-main">
            <span className="step5-choice-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 21h18" />
                <path d="M3 10h18" />
                <path d="M5 6l7-3 7 3" />
                <path d="M4 10v11" />
                <path d="M20 10v11" />
                <path d="M8 14v4" />
                <path d="M12 14v4" />
                <path d="M16 14v4" />
              </svg>
            </span>
            <span className="step5-choice-body">
              <span className="step5-choice-title">
                {pending ? pendingTitle || t("finishSetupScreen.oneMoment") : ctaTitle}
              </span>
              <span className="step5-choice-sub">{ctaSub}</span>
            </span>
          </div>
          <div className="step5-choice-meta">
            <span className="step5-choice-meta-item">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <T k="finishSetupScreen.poweredByStripe" tags={{ 0: (c) => <span className="payout-stripe-mark">{c}</span> }} />
            </span>
            <span className="step5-choice-arrow" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </span>
          </div>
        </button>
      </div>

      <p className="finish-stripe-support">
        <T k="finishSetupScreen.needHelpContactSupport" tags={{ 0: (c) => <a href="mailto:support@requesta.app">{c}</a> }} />
      </p>

      <p className="step5-trust-footer">
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
        {t("finishSetupScreen.bankLevelEncryptionYour")}
      </p>
    </div>
  </div>
);

export default FinishSetupScreen;
